/* eslint-disable @typescript-eslint/no-explicit-any */
import type { UsersClients } from 'src/graphql/client/generated-types';

import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxPermissionsService } from 'ngx-permissions';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Subscription } from 'rxjs';
import { Animations } from 'src/app/shared/animations/animations';
import { IUserPermission } from 'src/app/shared/interfaces/permissions.interface';
import { PermissionsStorageService } from 'src/app/shared/services/permissions-storage.service';
import { SettingsGQLService } from 'src/app/shared/services/settings/settings.graphql.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';
import { UserGQLService } from 'src/app/shared/services/users/user.graphql.service';
import { LoginInputSchema } from 'src/graphql/validation/schema';

import { AuthService } from '../../shared/services/auth.service';
import { TwoFactorAuthenticationModalComponent } from './two-factor-authentication-modal.component';

@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  animations: [Animations.leftRightSlider],
  styleUrls: ['./login-page.component.scss'],
})
export class LoginPageComponent implements OnInit, OnDestroy {
  user: UsersClients;
  twoFactorSecret: string;
  loginForm: UntypedFormGroup;
  isLoading = false;
  errorMessage: string;
  isHidden = false;

  counter = 0;

  subscriptions: Subscription = new Subscription();

  constructor(
    private modalService: NgbModal,
    private title: Title,
    private router: Router,
    private auth: AuthService,
    private toastrService: ToastrService,
    private userStateService: UserStateService,
    private userGQLService: UserGQLService,
    private settingsGqlService: SettingsGQLService,
    private permissionsService: NgxPermissionsService,
    private permissionsStorageService: PermissionsStorageService,
  ) {
    this.title.setTitle('Secure Connect');
  }

  ngOnInit() {
    this.toastrService.clear();
    this.loginForm = new UntypedFormGroup({
      username: new UntypedFormControl(undefined, [Validators.required]),
      password: new UntypedFormControl(undefined, [
        Validators.required,
        Validators.minLength(6),
      ]),
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  redirectToDashboard() {
    // @ts-expect-error - IUser doesn't map to the GraphQL type (GraphQL has the proper type). TODO: fix this
    this.userStateService.user = this.user;

    const userPermissions: IUserPermission[] = this.user
      .permissions as IUserPermission[];
    const permissions: string[] = userPermissions.map(
      (permission: IUserPermission) => {
        return permission.name;
      },
    );

    this.permissionsService.addPermission(permissions);
    this.permissionsService.loadPermissions(permissions);

    this.permissionsStorageService.permissions = permissions;

    window.location.href = './dashboard/home';
  }

  openTwoFactorAuthenticationModal() {
    const modalRef = this.modalService.open(
      TwoFactorAuthenticationModalComponent,
    );
    modalRef.componentInstance.modalRef = modalRef;

    modalRef.componentInstance.twoFactorEnabled.subscribe(
      (success: boolean) => {
        if (success) {
          this.redirectToDashboard();
        }
      },
    );
  }

  async onSubmit() {
    this.loginForm.disable();
    this.isLoading = true;

    try {
      const data = LoginInputSchema().parse(this.loginForm.value);
      const { data: loginResult } = await firstValueFrom(this.auth.login(data));

      this.userStateService.userCredentials = data;

      if (loginResult?.login?.success) {
        await this.waitForCookie('SC_AUTH');

        const user = await firstValueFrom(
          this.userGQLService.getLoggedInUser(),
        );

        // @ts-expect-error - IUser doesn't map to the GraphQL type (GraphQL has the proper type). TODO: fix this
        this.user = user;
        this.userStateService.user = user;

        const twoFactorResult = await firstValueFrom(
          this.settingsGqlService.isTwoFactorAuthenticationEnabled(),
        );
        console.log('DATA:', twoFactorResult);

        if (twoFactorResult.data !== undefined) {
          const isTwoFactorEnabled =
            twoFactorResult.data?.isTwoFactorAuthenticationEnabled;

          if (isTwoFactorEnabled) {
            this.openTwoFactorAuthenticationModal();
          } else {
            const userPermissions = user.permissions;
            const permissions = userPermissions?.map(
              (permission: IUserPermission) => {
                return permission.name;
              },
            );

            if (permissions) {
              this.permissionsService.addPermission(permissions);
              this.permissionsService.loadPermissions(permissions);

              this.permissionsStorageService.permissions = permissions;
            }

            this.redirectToDashboard();
          }
        }
      }
    } catch (error) {
      console.log('ERROR:', error);
      this.isLoading = false;

      const errorDescription = error?.error?.error_description;
      this.errorMessage = errorDescription ?? 'Unknown service error';

      this.loginForm.enable();
    } finally {
      this.isLoading = false;
    }
  }

  async waitForCookie(cookieName: string, maxWaitTime = 5000) {
    const interval = 100;
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
      const cookieExists = document.cookie.split(';').some((cookie) => {
        return cookie.trim().startsWith(`${cookieName}=`);
      });
      if (cookieExists) {
        return;
      }

      await new Promise((res) => {
        return setTimeout(res, interval);
      });
    }

    throw new Error(
      'Authentication cookie was not set within the expected time.',
    );
  }

  onNext() {
    this.isHidden = !this.isHidden;
    this.counter++;
  }

  onPrevious() {
    this.isHidden = !this.isHidden;
    if (this.counter > 0) {
      this.counter--;
    }
  }

  setLoading(value: any) {
    if (typeof value === 'boolean') {
      this.isLoading = value;
    } else {
      console.error('Expected a boolean, but got:', value);
    }
  }
}
